// = Libs =
import { ChangeEvent, FC, ReactElement, SyntheticEvent } from "react";
import Checkbox from "@/common/components/ui/checkbox/checkbox";
import Input from "@/common/components/ui/input/input";
import { Field, FieldProps } from "formik";
import FormError from "@/components/form/components/form-error/form-error";
import { FieldInputProps, FormikProps } from "formik/dist/types";
import CustomDatePicker from "@/common/components/ui/date/customDatePicker";
import Icon from "@/common/components/ui/icon/icon";

// = Styles =
import classes from "./custom-input.module.scss";
import RichText from "@/common/components/ui/rich-text/rich-text";
import Select from "@/common/components/ui/select/select";
import { OptionModel } from "@/common/components/ui/select/select.types";
import FileUploader from "@/common/components/ui/file-uploader/file-uploader";

// = Imports =
type InputTypeType =
  | 'checkbox'
  | 'toggle'
  | 'radio'
  | 'select'
  | 'multiple'
  | 'multiple-select'
  | 'calendar'
  | 'period'
  | 'text'
  | 'rich-text'
  | 'number'
  | 'file';

interface InputFieldType {
  value: any;
  inputType: InputTypeType;
  text: string;
  onChange?: (newData: string | string[] | number | boolean | undefined) => any;
  config?: any & { options: OptionModel[] };
  className?: string;
  formik: boolean;

  placeholder?: string;
  required?: boolean;
  mask?: string;
  name: string;
  fullWidth?: boolean;
  border?: string;
  onChangeEvent?: (event: ChangeEvent) => any;
  onBlur?: (event: ChangeEvent) => any;
  onClick?: (event: SyntheticEvent) => any;
  disabled?: boolean;
  min?: string;
  max?: string;
  isId?: boolean;
}

const renderSwitch = (
  props: InputFieldType,
  field?: FieldInputProps<any>,
  form?: FormikProps<any>
) => {
  switch (props.inputType) {
    // TODO: пройтись по компонентам в части стилей и типов, дополнить список
    case 'checkbox':
      return (
        <Checkbox
          {...field}
          onChange={undefined}
          {...props}
          value={Boolean(field?.value)}
          onChangeEvent={field?.onChange}
        />
      );
    case 'text':
      return (
        <Input
          {...field}
          {...props}
          placeholder={props.text}
          value={props.value as string}
          onChangeEvent={field?.onChange}
        />
      );
    case 'number':
      return (
        <Input
          {...field}
          {...props}
          placeholder={props.text}
          value={props.value as string}
          onChangeEvent={field?.onChange}
          type={'number'}
        />
      );
    case 'select':
      return (
        <Select
          {...field}
          {...props}
          placeholder={props.text}
          value={props.config?.options?.find(
            (option: OptionModel) =>
              option.value === (field?.value || props.value)
          )}
          onChange={(option: OptionModel | OptionModel[]) => {
            form?.setFieldValue(
              field?.name as string,
              (option as OptionModel).value
            );
            props.onChange && props.onChange((option as OptionModel).value);
          }}
          options={props.config?.options}
        />
      );
    case 'multiple-select':
      return (
        <Select
          {...field}
          {...props}
          placeholder={props.text}
          value={props.config?.options?.filter(
            (option: OptionModel) =>
              (field?.value || props.value || []).indexOf(option.value) >= 0
          )}
          onChange={(option: OptionModel | OptionModel[]) => {
            form?.setFieldValue(
              field?.name as string,
              (option as OptionModel[]).map((item: OptionModel) => item.value)
            );
            props.onChange &&
              props.onChange(
                (option as OptionModel[]).map(
                  (item: OptionModel) => item.value
                ) as string[]
              );
          }}
          options={props.config?.options}
          isMulti
        />
      );
    case 'rich-text':
      return (
        <RichText
          {...props}
          {...field}
          placeholder={props.text}
          value={field?.value}
          onChange={(val) => form?.setFieldValue(props.name, val)}
          onBlur={() => {
            form?.setFieldTouched(props.name);
          }}
        />
      );
    case 'calendar':
      return (
        <CustomDatePicker
          {...field}
          {...props}
          value={field?.value}
          placeholder={props.text}
          onChange={(val) => form?.setFieldValue(props.name, val)}
        />
      );
    case 'file':
      return (
        <FileUploader
          {...field}
          {...props}
          onChangeId={(val) => {
            form?.setFieldValue(props.name, val);
            props.onChange && props.onChange(val);
          }}
          isNew={
            !(props.value && props.value !== -1) &&
            !(field?.value && field.value !== -1)
          }
          initialId={props.value || field?.value}
        />
      );
  }
  return (
    <Input
      {...field}
      onChange={props.onChange}
      value={props.value as string}
      placeholder={props.text}
      className={props.className}
    />
  );
};

const CustomInput: FC<InputFieldType> = (
  props = {
    name: '',
    value: '',
    inputType: 'checkbox',
    text: 'string',
    onChange: (e: string | string[] | number | boolean | undefined) => {
      return {};
    },
    className: '',
    formik: false,
  }
) => {
  return props.formik ? (
    <Field name={props.name}>
      {(params: FieldProps) => {
        const { field, form, meta } = params;
        return (
          <div className={classes.input}>
            {renderSwitch(props, field, form)}
            {meta.touched && meta.error && (
              <FormError text={String(meta.error)} />
            )}
            {props.required && (
              <span className={classes.required}>
                <Icon type="asterisk" color="secondary" />
              </span>
            )}
          </div>
        );
      }}
    </Field>
  ) : (
    (renderSwitch({ ...props }) as ReactElement)
  );
};
export default CustomInput;

export type { InputTypeType };
